// Feedback videos — participants talking about a training — shown on that
// training's landing page. Stored as a JSON list on the Training row
// (`feedbackVideos`), edited in the training dialog's «فيديوهات الآراء» tab.
//
// Shared by the admin, the tRPC input and the landing page, so all three agree
// on what a video looks like and on how a pasted link is played.

import { z } from "zod";
import { youTubeId } from "#/lib/youtube";

/** A long list would turn the section into a wall the visitor scrolls past. */
export const MAX_FEEDBACK_VIDEOS = 24;

export const feedbackVideoSchema = z.object({
	id: z.string().trim().min(1).max(40),
	// "upload": a file in our bucket; "link": pasted by the admin.
	source: z.enum(["upload", "link"]),
	// Rendered into <video src> and <a href>, so http(s) only — never
	// javascript: or data:.
	url: z
		.string()
		.trim()
		.max(500)
		.refine(isSafeVideoLink, "رابط الفيديو يجب أن يبدأ بـ https://"),
	// Who is speaking, e.g. "Amina — fondatrice d'une startup". Optional.
	title: z.string().trim().max(120).default(""),
});

export const feedbackVideosSchema = z
	.array(feedbackVideoSchema)
	.max(MAX_FEEDBACK_VIDEOS);

export type FeedbackVideo = z.infer<typeof feedbackVideoSchema>;

/**
 * The column is plain JSON, so whatever is stored is re-checked on the way
 * out: one malformed entry is dropped rather than taking the page down.
 */
export function asFeedbackVideos(value: unknown): FeedbackVideo[] {
	if (!Array.isArray(value)) return [];
	return value.flatMap((item) => {
		const parsed = feedbackVideoSchema.safeParse(item);
		return parsed.success ? [parsed.data] : [];
	});
}

const VIDEO_FILE = /\.(mp4|webm|mov|m4v)(\?|#|$)/i;

/**
 * How a video plays on the landing page:
 * - YouTube (any URL form) → the lazy 9:16 player the video template uses
 * - an uploaded file, or a link straight to a video file → a native <video>
 * - anything else (Facebook, Instagram, Drive…) can't be embedded reliably,
 *   so it becomes a card that opens the link in a new tab
 */
export type FeedbackVideoPlayback =
	| { kind: "youtube"; videoId: string }
	| { kind: "file"; src: string }
	| { kind: "external"; href: string };

export function feedbackVideoPlayback(
	video: Pick<FeedbackVideo, "source" | "url">,
): FeedbackVideoPlayback {
	if (video.source === "upload") return { kind: "file", src: video.url };
	const videoId = youTubeId(video.url);
	if (videoId) return { kind: "youtube", videoId };
	if (VIDEO_FILE.test(video.url)) return { kind: "file", src: video.url };
	return { kind: "external", href: video.url };
}

/** Only http(s) links are accepted from the admin — never javascript: etc. */
export function isSafeVideoLink(value: string) {
	try {
		const url = new URL(value.trim());
		return url.protocol === "https:" || url.protocol === "http:";
	} catch {
		return false;
	}
}
